import * as React from "react"
import { useState, useEffect } from "react"
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import { useDropzone } from "react-dropzone"
import { CircularProgress } from "@mui/material"
import { useNavigate } from "react-router-dom"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import { downscale, dataURLToBlob } from "../../utils/imageFunctions"
import "react-html5-camera-photo/build/css/index.css"
import Images from "./Images"
import ImageIcon from "@mui/icons-material/Image"
import KeypointImage from "./KeypointImage"
import OverlayImage from "./OverlayImage"
import { Slider } from "@mui/material"

const TARGET = "1"
const SOURCE = "2"

const MATCHER_DEFAULTS = {
  sift: { threshold: 0.75 },
  orb: { threshold: 0.75 },
  faiss: { threshold: 0.75, max_dim: 1024, k: 10 },
}
function MyDropzone({
  image,
  onDropFile,
  defaultImage = "/images/kilter.jpg",
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    // Accept only images
    accept: "image/*",

    onDrop: (acceptedFiles) => {
      // Do something with the files
      console.log(acceptedFiles)
      onDropFile(acceptedFiles[0])
    },
  })

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {
        <img
          src={image || defaultImage}
          alt="snapshot"
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        />
      }
    </div>
  )
}

function KeypointTab() {
  const [state, setState] = useState({
    images: [],
    keypoints1: [],
    keypoints2: [],
    matches: [],
    img1Shape: [],
    warpedImage: null,
    trainImage: null,
  })
  const [images, setImages] = useState({
    [TARGET]: null,
    [SOURCE]: null,
  })
  const [selectedFiles, setSelectedFiles] = useState({
    [TARGET]: null,
    [SOURCE]: null,
  })
  const [method, setMethod] = useState("faiss")
  const [matcherParams, setMatcherParams] = useState(MATCHER_DEFAULTS)
  const [loading, setLoading] = useState(false)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedKeypoints, setSelectedKeypoints] = useState([])
  const navigate = useNavigate()

  const handleCapture = (event) => {
    const file = event.target.files[0]
    const reader = new FileReader()
    reader.onloadend = () => {
      downscale(reader.result, 1024).then((resizedImage) => {
        setImage(resizedImage)
        const blob = dataURLToBlob(resizedImage)
        const newFile = new File([blob], file.name, { type: blob.type })
        setSelectedFile(newFile)
      })
    }
    reader.readAsDataURL(file)
  }

  const fetchKeypoints = async (targetFile, sourceFile) => {
    if (targetFile) {
      console.log(targetFile)
      setLoading(true)
      const formData = new FormData()
      formData.append("target", targetFile)
      if (sourceFile) {
        formData.append("source", sourceFile)
      }
      const params = matcherParams[method]
      formData.append("method", method)
      formData.append("threshold", params.threshold)
      if (method === "faiss") {
        formData.append("max_dim", params.max_dim)
        formData.append("k", params.k)
      }
      console.info("MATCHER: ", method, params)

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || ""}/api/display-keypoints`,
          {
            method: "POST",
            body: formData,
          }
        )
        const json = await response.json()
        console.info(json)
        const encodedImages = json.images
        const decodedImages = encodedImages.map((encodedImage) => {
          return `data:image/png;base64,${encodedImage}`
        })
        const [warpedImage, trainImage] = [json.warped_image, json.train_image].map(
          (encodedImage) => {
            return `data:image/png;base64,${encodedImage}`
          }
        )

        const { keypoints1, keypoints2, matches, img1_shape, matches_mask } =
          json
        setState({
          ...state,
          images: decodedImages,
          img1Shape: img1_shape,
          keypoints1,
          keypoints2,
          matches,
          matchesMask: matches_mask,
          warpedImage,
          trainImage,
        })
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    console.info("SELECTED FILES: ", selectedFiles)
    if (!selectedFiles[TARGET]) return
    fetchKeypoints(selectedFiles[TARGET], selectedFiles[SOURCE])
  }, [selectedFiles, method, matcherParams])

  const setImage = (key) => (image) => {
    setImages({ ...images, [key]: image })
  }

  const setFile = (key) => (file) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      downscale(reader.result, 1024).then((resizedImage) => {
        setImage(key)(resizedImage)
        const blob = dataURLToBlob(resizedImage)
        const newFile = new File([blob], file.name, { type: blob.type })
        setSelectedFiles({ ...selectedFiles, [key]: newFile })
      })
    }
    reader.readAsDataURL(file)
  }

  const handleKeypointClick = (matchIndex) => {
    if (!selectionMode) return
    
    const match = state.matches[matchIndex]
    const { queryIdx, trainIdx } = match
    const kp1 = state.keypoints1[queryIdx]
    const kp2 = state.keypoints2[trainIdx]
    
    // Check if this keypoint pair is already selected
    const isAlreadySelected = selectedKeypoints.some(
      (kp) => kp.queryIdx === queryIdx && kp.trainIdx === trainIdx
    )
    
    if (isAlreadySelected) {
      // Remove if already selected
      setSelectedKeypoints(
        selectedKeypoints.filter(
          (kp) => !(kp.queryIdx === queryIdx && kp.trainIdx === trainIdx)
        )
      )
    } else {
      // Add to selection
      setSelectedKeypoints([
        ...selectedKeypoints,
        {
          queryIdx,
          trainIdx,
          query: { x: kp1.pt[0], y: kp1.pt[1] },
          train: { x: kp2.pt[0], y: kp2.pt[1] },
        },
      ])
    }
  }

  const getCoordinatesText = () => {
    const queryPoints = selectedKeypoints
      .map((kp) => `    [${kp.query.x.toFixed(2)}, ${kp.query.y.toFixed(2)}]`)
      .join(",\n")
    
    const trainPoints = selectedKeypoints
      .map((kp) => `    [${kp.train.x.toFixed(2)}, ${kp.train.y.toFixed(2)}]`)
      .join(",\n")
    
    return `query_keypoints = [\n${queryPoints}\n]\n\ntrain_keypoints = [\n${trainPoints}\n]`
  }

  const handleCopyToClipboard = () => {
    const text = getCoordinatesText()
    navigator.clipboard.writeText(text).then(
      () => {
        console.log("Copied to clipboard")
      },
      (err) => {
        console.error("Failed to copy: ", err)
      }
    )
  }

  const handleClearSelection = () => {
    setSelectedKeypoints([])
  }

  const params = matcherParams[method]
  const updateParam = (paramName) => (value) => {
    setMatcherParams({
      ...matcherParams,
      [method]: { ...matcherParams[method], [paramName]: value },
    })
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="matcher-select-label">Matcher</InputLabel>
            <Select
              labelId="matcher-select-label"
              value={method}
              label="Matcher"
              onChange={(e) => setMethod(e.target.value)}
            >
              <MenuItem value="sift">SIFT</MenuItem>
              <MenuItem value="orb">ORB</MenuItem>
              <MenuItem value="faiss">FAISS</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">
            Ratio threshold: {params.threshold}
          </Typography>
          <Slider
            value={params.threshold}
            onChangeCommitted={(event, newValue) =>
              updateParam("threshold")(newValue)
            }
            step={0.01}
            min={0.1}
            max={1}
            valueLabelDisplay="auto"
            size="small"
          />
        </Grid>
        {method === "faiss" && (
          <>
            <Grid item xs={6} md={2}>
              <TextField
                label="max_dim"
                type="number"
                size="small"
                fullWidth
                value={params.max_dim}
                onChange={(e) =>
                  updateParam("max_dim")(parseInt(e.target.value, 10) || 0)
                }
                inputProps={{ min: 128, step: 64 }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="k"
                type="number"
                size="small"
                fullWidth
                value={params.k}
                onChange={(e) =>
                  updateParam("k")(parseInt(e.target.value, 10) || 2)
                }
                inputProps={{ min: 2, step: 1 }}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={2}>
          <MyDropzone image={images[TARGET]} onDropFile={setFile(TARGET)} />
        </Grid>
        <Grid item xs={12} md={2}>
          <MyDropzone image={images[SOURCE]} onDropFile={setFile(SOURCE)} />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={selectionMode}
                onChange={(e) => setSelectionMode(e.target.checked)}
                color="primary"
              />
            }
            label="Keypoint Selection Mode"
          />
          {selectionMode && (
            <Typography variant="body2" color="text.secondary">
              Click on any keypoint to add/remove it from the selection
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {loading ? (
            <CircularProgress />
          ) : (
            <KeypointImage
              imageUrl={state?.images[0]}
              keypoints1={state?.keypoints1}
              keypoints2={state?.keypoints2}
              matches={state?.matches}
              img1Shape={state?.img1Shape}
              matchesMask={state?.matchesMask}
              onKeypointClick={handleKeypointClick}
              selectionMode={selectionMode}
              selectedKeypoints={selectedKeypoints}
            />
          )}
        </Grid>
        {selectionMode && selectedKeypoints.length > 0 && (
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Selected Keypoints ({selectedKeypoints.length})
              </Typography>
              <TextField
                multiline
                fullWidth
                rows={10}
                value={getCoordinatesText()}
                InputProps={{
                  readOnly: true,
                }}
                onClick={handleCopyToClipboard}
                sx={{ 
                  cursor: "pointer",
                  "& .MuiInputBase-input": {
                    cursor: "pointer"
                  }
                }}
                helperText="Click to copy to clipboard"
              />
              <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleCopyToClipboard}
                  size="small"
                >
                  Copy to Clipboard
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearSelection}
                  size="small"
                >
                  Clear Selection
                </Button>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {loading ? (
            <CircularProgress />
          ) : (
            <OverlayImage
              image1Url={state?.trainImage}
              image2Url={state?.warpedImage}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default KeypointTab
